import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ApiError {
  message: string;
  status: number;
  errors?: any;
}

@Injectable({ providedIn: 'root' })
export class HttpService {
  private apiUrl: string;

  constructor(private http: HttpClient) {
    // this.apiUrl = environment.apiBaseUrl
    this.apiUrl = this.determineApiUrl();
    console.log('API Base URL:', this.apiUrl);
  }

  private determineApiUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    if (hostname === '192.168.29.15' || hostname === '192.168.29.12') {
      // return 'http://192.168.29.12:8080/api';
       return 'https://web-trex-dev-fe.azurewebsites.net/api';
    }
    if (
      hostname === 'localhost' ||
      hostname === 'web-trex-dev-fe.azurewebsites.net'
    ) {
      return 'https://web-trex-dev-api.azurewebsites.net/api';
    }

    if (hostname === 'web-trex-prd-fe-staging.azurewebsites.net') {
      return 'https://web-trex-prd-api-staging.azurewebsites.net/api';
    }

    if (hostname === 'trex.constructionpathways.com.au') {
      return 'https://web-trex-prd-api.azurewebsites.net/api';
    }

    return 'https://web-trex-dev-api.azurewebsites.net/api';
  }

  private getHeaders(
    isFormData = false,
    excludeAuthToken = false,
  ): HttpHeaders {
    let headers = new HttpHeaders().set('Accept', 'application/json');

    if (!isFormData) {
      headers = headers.set('Content-Type', 'application/json');
    }

    if (!excludeAuthToken) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  private logUrl(method: string, endpoint: string): void {
    const fullUrl = `${this.apiUrl}/${endpoint}`;
  }

  setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  clearToken(): void {
    localStorage.removeItem('auth_token');
  }

  get<T>(endpoint: string): Observable<T> {
    this.logUrl('GET', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .get<T>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  getWithoutAuth<T>(endpoint: string): Observable<T> {
    this.logUrl('GET (No Auth)', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .get<T>(url, {
        headers: this.getHeaders(false, true),
      })
      .pipe(catchError(this.handleError));
  }

  post<T>(endpoint: string, data: any, isFormData = false): Observable<T> {
    this.logUrl('POST', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .post<T>(url, data, {
        headers: this.getHeaders(isFormData),
      })
      .pipe(catchError(this.handleError));
  }

  postWithoutAuth<T>(
    endpoint: string,
    data: any,
    isFormData = false,
  ): Observable<T> {
    this.logUrl('POST (No Auth)', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .post<T>(url, data, {
        headers: this.getHeaders(isFormData, true),
      })
      .pipe(catchError(this.handleError));
  }

  put<T>(endpoint: string, data: any, isFormData = false): Observable<T> {
    this.logUrl('PUT', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .put<T>(url, data, {
        headers: this.getHeaders(isFormData),
      })
      .pipe(catchError(this.handleError));
  }

  delete<T>(endpoint: string): Observable<T> {
    this.logUrl('DELETE', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http
      .delete<T>(url, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  download(endpoint: string): Observable<HttpResponse<Blob>> {
    this.logUrl('DOWNLOAD', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;

    const headers = this.getHeaders(true).set('Accept', 'application/pdf');

    return this.http
      .get(url, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }

  downloadWithoutAuth(endpoint: string): Observable<HttpResponse<Blob>> {
    this.logUrl('DOWNLOAD (No Auth)', endpoint);
    const url = `${this.apiUrl}/${endpoint}`;

    const headers = this.getHeaders(true, true).set(
      'Accept',
      'application/pdf',
    );

    return this.http
      .get(url, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }

  getApiUrl(): string {
    return this.apiUrl;
  }

  private handleError(error: HttpErrorResponse) {
    let userMessage: string;

    if (error.error instanceof ErrorEvent) {
      userMessage = 'Something went wrong. Please try again later.';
    } else if (error.status === 429) {
      // Toast already shown by networkInterceptor — pass a silent marker so components skip their own toast
      userMessage = '';
    } else if (error.status >= 500) {
      userMessage = 'Something went wrong. Please try again later.';
    } else if (error.status === 0) {
      userMessage = 'Something went wrong. Please try again later.';
    } else {
      userMessage =
        error.error?.message ||
        error.error?.error ||
        error.message ||
        `Server error: ${error.status}`;
    }

    const apiError: ApiError = {
      message: userMessage,
      status: error.status,
      errors: error.error?.errors || error.error,
    };

    return throwError(() => apiError);
  }
}
